import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import Card from 'components/card/Card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import Loader from 'components/Loader';
import { typography } from 'theme/tokens';
import { tableCellProps, tableHeaderTextProps, tableProps, tableRowProps, tableThFlexProps, tableThProps } from 'theme/constants';
import { translate } from '../../../i18n';
import { AccessLogDto, AccessLogEventType } from '../../../models/access-control/dto/get-access-logs.dto';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetAccessLogs } from '../hooks/useGetAccessLogs';
import { getAccessLogEventColorScheme } from '../utils/access-control.utils';

const columnHelper = createColumnHelper<AccessLogDto>();

interface AccessLogsTableProps {
  /**
   * Fiche-client mode (rollout item 9) — scopes the log view to one player
   * (e.g. their Enzo Card HYROX/plateau passages) instead of the whole org.
   * Zone/period filters stay available; the player column is hidden since
   * it would just repeat the page it's embedded on.
   */
  userId?: string;
  /** Overrides the default "Access Logs" card title — used when embedded on a player's fiche client. */
  title?: string;
}

export default function AccessLogsTable({ userId, title }: AccessLogsTableProps = {}) {
  const { selectedOrganization } = useSelectedOrganization();
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 20;

  const [doorIdInput, setDoorIdInput] = useState('');
  const [debouncedDoorId, setDebouncedDoorId] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<AccessLogEventType | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDoorId(doorIdInput), 300);
    return () => clearTimeout(timer);
  }, [doorIdInput]);

  useEffect(() => {
    setCurrentPage(0);
    // `userId` matters here: React Router doesn't remount UserPage when only
    // its `?user=` query string changes (many pages link fiche-client to
    // fiche-client via useNavigateToUserDetails), so without this dependency
    // an admin who paginated to page 2 for one player and then clicked
    // through to another would silently request that other player's page 2
    // instead of resetting to their most recent passages.
  }, [debouncedDoorId, eventTypeFilter, dateFrom, dateTo, selectedOrganization?.id, userId]);

  const { data, isLoading, isInitialLoading, isFetching, isError } = useGetAccessLogs(selectedOrganization?.id, {
    page: currentPage,
    limit,
    doorId: debouncedDoorId || undefined,
    userId,
    eventType: eventTypeFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  // `enabled: !!organizationId`. This table already guarded on
  // `!selectedOrganization`, which is a COMPLETE re-derivation of that single
  // gate and so failed safe — but a store-derived gate is the fragile pattern
  // D62 warns about (add a second `enabled:` term and the guard silently spins
  // forever). Route the never-issued case through the query's own state so it
  // cannot drift from the gate.
  const readState = classifyRead(isError, isInitialLoading, data);

  const hasActiveFilters = !!(debouncedDoorId || eventTypeFilter || dateFrom || dateTo);

  const columns = useMemo(
    () => [
      columnHelper.accessor('occurredAt', {
        id: 'occurredAt',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.occurred_at' as any)}</Text>,
        cell: (info) => (
          <Text color={textColor} fontSize="sm">
            {new Date(info.getValue()).toLocaleString()}
          </Text>
        ),
      }),
      // Hidden when scoped to one player (`userId` set) — it would just
      // repeat the fiche-client page this table is embedded on. Shown in the
      // org-wide "vue club" so an operator can see who passed.
      ...(userId
        ? []
        : [
            columnHelper.accessor('userName', {
              id: 'userName',
              header: () => <Text {...tableHeaderTextProps}>{translate('access_control.player' as any)}</Text>,
              cell: (info) => (
                <Text color={textColor} fontSize="sm">
                  {info.getValue() || '—'}
                </Text>
              ),
            }),
          ]),
      columnHelper.accessor('doorId', {
        id: 'doorId',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.door_id' as any)}</Text>,
        cell: (info) => (
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor('eventType', {
        id: 'eventType',
        header: () => <Text {...tableHeaderTextProps}>{translate('access_control.event_type' as any)}</Text>,
        cell: (info) => (
          <Badge colorScheme={getAccessLogEventColorScheme(info.getValue())}>
            {translate(`access_control.event_${info.getValue()}` as any)}
          </Badge>
        ),
      }),
    ],
    [textColor, userId],
  );

  const table = useReactTable({
    data: data?.logs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (data?.pagination.hasNextPage) setCurrentPage(currentPage + 1);
  };

  const clearFilters = () => {
    setDoorIdInput('');
    setEventTypeFilter('');
    setDateFrom('');
    setDateTo('');
  };

  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center" flexWrap="wrap" gap={2}>
        <Text color={textColor} {...typography.pageTitle}>
          {title ?? translate('access_control.logs_title' as any)}
        </Text>

        {data?.pagination && (
          <HStack spacing={4}>
            <Text fontSize="sm" color={secondaryTextColor}>
              {translate('game.page_of')} {currentPage + 1} {translate('game.of')} {data.pagination.totalPages}
              {data.pagination.totalCount > 0 && ` (${data.pagination.totalCount} ${translate('game.total')})`}
            </Text>
            <HStack spacing={2}>
              <Button size="sm" onClick={handlePreviousPage} isDisabled={currentPage === 0 || isLoading || isFetching} variant="outline">
                {translate('game.previous')}
              </Button>
              <Button size="sm" onClick={handleNextPage} isDisabled={!data.pagination.hasNextPage || isLoading || isFetching} variant="outline">
                {translate('game.next')}
              </Button>
            </HStack>
          </HStack>
        )}
      </Flex>

      <Flex px="25px" pb="15px" gap={3} flexWrap="wrap" align="center">
        <Input
          placeholder={translate('access_control.filter_door_id' as any)}
          value={doorIdInput}
          onChange={(e) => setDoorIdInput(e.target.value)}
          size="sm"
          maxW="200px"
        />
        <Select
          placeholder={translate('access_control.all_event_types' as any)}
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value as AccessLogEventType | '')}
          size="sm"
          maxW="160px"
        >
          {Object.values(AccessLogEventType).map((eventType) => (
            <option key={eventType} value={eventType}>
              {translate(`access_control.event_${eventType}` as any)}
            </option>
          ))}
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} size="sm" maxW="160px" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} size="sm" maxW="160px" />
      </Flex>

      <Box>
        {readState === 'loading' ? (
          <Loader isLoading variant="spinner" />
        ) : readState === 'unreadable' ? (
          // `unreadable`, not the raw `error` object: a BACKGROUND refetch that
          // fails over logs already on screen must keep them rather than
          // replacing an operator's page of audit rows with an error block
          // because a 30s refresh 403'd (R96-3). `classifyRead` delegates that
          // to `isUnreadable`'s `!data` half.
          <ErrorState message={translate('access_control.error_loading_logs' as any)} />
        ) : !data?.logs?.length ? (
          hasActiveFilters ? (
            <EmptyState
              variant="no-results-filter"
              title={translate('access_control.no_logs_found' as any)}
              activeFilters={[
                ...(debouncedDoorId ? [debouncedDoorId] : []),
                ...(eventTypeFilter ? [translate(`access_control.event_${eventTypeFilter}` as any)] : []),
              ]}
              onClearAllFilters={clearFilters}
            />
          ) : (
            <Box textAlign="center" py={8}>
              <Text color={secondaryTextColor} fontSize="lg">
                {translate(
                  (userId
                    ? 'access_control.no_player_passages_found'
                    : 'access_control.no_logs_found') as any,
                )}
              </Text>
            </Box>
          )
        ) : (
          <Table {...tableProps} opacity={isFetching ? 0.5 : 1} transition="opacity 0.2s">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} colSpan={header.colSpan} {...tableThProps} borderColor={borderColor}>
                      <Flex {...tableThFlexProps}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id} {...tableRowProps} _hover={{}} cursor="default">
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} {...tableCellProps}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Card>
  );
}
